const bootstrapConfig = window.CentreCapAppearance ?? {};
const storageKey = bootstrapConfig.storageKey ?? 'centre-cap:appearance';
const themeColors = bootstrapConfig.themeColors ?? { dark: '#0c0e10', light: '#f4f4f2' };
const appearanceEvent = 'centre-cap:appearance-change';
const root = document.documentElement;

const isAppearance = (value) => value === 'dark' || value === 'light';

function syncBrowserChrome(appearance) {
  const color = themeColors[appearance];
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', color);

  const header = document.querySelector('#header-component');
  if (!header) return;

  const normalizeColor = (value) => value.toLowerCase().replaceAll(' ', '').replaceAll(',', '');
  const paletteColors = Object.values(themeColors).map(normalizeColor);
  const currentColor = header.dataset.themeColor ? normalizeColor(header.dataset.themeColor) : undefined;
  const normalizedPaletteColors = paletteColors.flatMap((value) => {
    const hex = value.replace('#', '');
    const rgb = hex.match(/.{2}/g)?.map((part) => Number.parseInt(part, 16));
    return rgb ? [value, normalizeColor(`rgb(${rgb.join(',')})`)] : [value];
  });

  if (!currentColor || normalizedPaletteColors.includes(currentColor)) {
    const hex = color.replace('#', '');
    const rgb = hex.match(/.{2}/g)?.map((part) => Number.parseInt(part, 16));
    header.dataset.themeColor = rgb ? `rgb(${rgb.join(', ')})` : color;
  }
}

function applyAppearance(appearance, { persist = false } = {}) {
  if (!isAppearance(appearance)) return;

  root.dataset.appearance = appearance;
  root.style.colorScheme = appearance;
  syncBrowserChrome(appearance);

  if (persist) {
    try {
      localStorage.setItem(storageKey, appearance);
    } catch (_) {
      // The visual switch still works when storage is unavailable.
    }
  }

  window.dispatchEvent(new CustomEvent(appearanceEvent, { detail: { appearance } }));
}

class CentreCapAppearanceSelector extends HTMLElement {
  #abortController;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('change', this.#onChange, { signal });
    window.addEventListener(appearanceEvent, this.#onAppearanceChange, { signal });
    this.#sync(root.dataset.appearance);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.checked) return;
    applyAppearance(input.value, { persist: true });
  };

  #onAppearanceChange = (event) => {
    this.#sync(event.detail?.appearance);
  };

  #sync(appearance) {
    if (!isAppearance(appearance)) return;
    this.dataset.selectedAppearance = appearance;

    for (const input of this.querySelectorAll('input[type="radio"]')) {
      input.checked = input.value === appearance;
    }
  }
}

if (!customElements.get('centre-cap-appearance-selector')) {
  customElements.define('centre-cap-appearance-selector', CentreCapAppearanceSelector);
}

window.addEventListener('storage', (event) => {
  if (event.key !== storageKey) return;
  applyAppearance(isAppearance(event.newValue) ? event.newValue : 'dark');
});

applyAppearance(isAppearance(root.dataset.appearance) ? root.dataset.appearance : 'dark');
